import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import {
  Event,
  EVENT_STATUS_COLORS,
  EVENT_STATUS_DISPLAY_NAMES,
  EventStatusEnum,
} from '../event'
import { ModuleColumnDefinition, ModuleTypeEnum } from '../module'

/**
 * Standard column definitions for the Event module
 */
export const EVENT_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'title',
    name: 'title' as keyof Event,
    label: 'Title',
    description: 'Title of the Event',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '160px',
    minWidth: '160px',
  },
  {
    id: 'description',
    name: 'description' as keyof Event,
    label: 'Description',
    description: 'Description of the Event',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.RICH_TEXT,
    width: '260px',
    minWidth: '220px',
  },
  {
    id: 'eventType',
    name: 'eventType' as keyof Event,
    label: 'Event Type',
    description: 'Type of event',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '160px',
    minWidth: '160px',
  },
  {
    id: 'owner',
    name: 'ownerInfo' as keyof Event,
    label: 'Owner',
    description:
      'Organization, client, project, deal, or person this event belongs to',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.REFERENCE,
    width: '200px',
    minWidth: '160px',
  },
  {
    id: 'status',
    name: 'status' as keyof Event,
    label: 'Status',
    description: 'Event status',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '140px',
    minWidth: '140px',
    options: Object.values(EventStatusEnum).map(status => ({
      value: status,
      label: EVENT_STATUS_DISPLAY_NAMES[status],
      color: EVENT_STATUS_COLORS[status],
    })),
  },
  {
    id: 'isRecurring',
    name: 'isRecurring' as keyof Event,
    label: 'Recurring',
    description: 'Part of a recurring calendar series',
    isVisible: true,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 5,
    fieldType: FieldTypeEnum.enum.BOOLEAN,
    width: '120px',
    minWidth: '110px',
  },
  {
    id: 'tags',
    name: 'tags' as keyof Event,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the event',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 90, // Positioned near end of standard columns, custom fields come after at 91+
    fieldType: FieldTypeEnum.enum.STRING,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'startTime',
    name: 'startTime' as keyof Event,
    label: 'Start Time',
    description: 'Start Time of Event',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '150px',
    minWidth: '150px',
  },
  {
    id: 'endTime',
    name: 'endTime' as keyof Event,
    label: 'End Time',
    description: 'End Time of Event',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '150px',
    minWidth: '150px',
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Event,
    label: 'Created Date',
    description: 'Date Event was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '150px',
    minWidth: '150px',
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Event,
    label: 'Last Updated',
    description: 'Date Event was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '150px',
    minWidth: '150px',
  },
]

/**
 * Helper to combine standard and custom fields
 */
export function getEventColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
): ModuleColumnDefinition[] {
  const standardColumns = EVENT_STANDARD_COLUMNS.map(column => ({
    ...column,
    isVisible:
      visibilityPreferences?.[column.id] !== undefined
        ? !!visibilityPreferences[column.id]
        : column.isVisible,
  }))

  const customColumns = customFields.map((field, index) => ({
    id: `custom_${field.fieldName}`,
    name: field.fieldName,
    label: field.label,
    description: `Custom field: ${field.label}`,
    isVisible:
      visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
        ? !!visibilityPreferences[`custom_${field.fieldName}`]
        : !!field.isActive,
    isRequired: false,
    isSystem: false,
    order: 91 + index, // Custom fields come after tags (order 90)
    fieldType: field.fieldType,
    width: '180px',
    minWidth: '180px',
  })) as ModuleColumnDefinition[]

  return [...standardColumns, ...customColumns]
}

export function getEventModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.EVENT
}
